import {ceil} from 'lodash';
import {t} from 'i18next';
import {isNullOrEmpty} from './HelperUtils';
import {config} from '../init/config';
import {WatermarkFileType} from '../models/WatermarkFileType';
import {PreviewFileType} from '../models/PreviewFileType';
import {DROPDOWN_OPTION} from './DropdownOptionPropsUtils';

/**
 * @name formatDate
 * @description
 * Utils function that formats a date (given as number, string or Date) and the current locale (region/defaultLocale)
 */
export const formatDate = (language: string, date: number | string | Date | null | undefined) => {
    if ((date !== undefined) && (language !== undefined) && (!isNullOrEmpty(language)) && date && (!isNullOrEmpty(date.toString()))) {
        let dateValue: number | Date;
        if (typeof date === 'string') {
            dateValue = Date.parse(date);
        } else {
            dateValue = date;
        }

        return new Intl.DateTimeFormat(language, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(dateValue);
    }

    return '-';
};

/**
 * @name formatSize
 * @description
 * Utils function that formats a size in bytes to the biggest sensible unit
 */
export const formatSize = (size: number | null | undefined): string => {
    if ((size === undefined) || (!size) || (size <= 0)) {
        return t('GENERAL_BYTES_TEXT', {size: 0});
    }

    let KB = 1024;
    let MB = KB * 1024;
    let GB = MB * 1024;
    let TB = GB * 1024;

    if (size < KB) {
        return t('GENERAL_BYTES_TEXT', {size: size});
    }

    if (size > TB) {
        return t('GENERAL_TB_TEXT', {size: ceil(size / TB, 3)});
    }

    if (size > GB) {
        return t('GENERAL_GB_TEXT', {size: ceil(size / GB, 2)});
    }

    if (size > MB) {
        return t('GENERAL_MB_TEXT', {size: ceil(size / MB, 1)});
    }

    if (size > KB) {
        return t('GENERAL_KB_TEXT', {size: ceil(size / KB, 0)});
    }

    return t('GENERAL_BYTES_TEXT', {size: 0});
};

/**
 * @name checkFileType
 * @param { string | null } filetype   The contentType information contained within the file's metadata
 * @description
 * Checks which contentType the provided item is, from its metadata
 *
 */
export const checkFiletype = (contentType: string | null | undefined): string => {
    if ((contentType === undefined) || (isNullOrEmpty(contentType))) {
        return 'other';
    }

    let regexImage = /image/i;
    let regexPdf = /pdf/i;
    let regexZip = /zip/i;
    let regexPresentation = /presentation/i;
    let regexWord = /word/i;
    let regexSheet = /sheet/i;
    let regexAudio = /audio/i;
    let regexVideo = /video/i;
    let regexFolder = /folder/i;

    contentType = contentType ?? 'other';
    switch (true) {
        case regexImage.test(contentType):
            return 'image';
        case regexPdf.test(contentType):
            return 'pdf';
        case regexZip.test(contentType):
            return 'zip';
        case regexPresentation.test(contentType):
            return 'presentation';
        case regexWord.test(contentType):
            return 'word';
        case regexSheet.test(contentType):
            return 'sheet';
        case regexAudio.test(contentType):
            return 'audio';
        case regexVideo.test(contentType):
            return 'video';
        case regexFolder.test(contentType):
            return 'folder';
        default:
            return 'other';
    }
};

/**
 * @name formatFiletype
 * @param { string | null } filetype   The filetype information contained within the file's metadata
 * @description
 * Formats the filetype contained within the file's metadata into a human-readable form
 * Uses the checkFiletype to initially check the filetype from the item's metadata
 */
export const formatFiletype = (contentType: string | null | undefined): string => {
    if ((contentType === undefined) || (isNullOrEmpty(contentType))) {
        return t(DROPDOWN_OPTION + 'OTHER');
    }

    let filetypeCheckResult = checkFiletype(contentType);
    switch (true) {
        case filetypeCheckResult === 'image':
            return t(DROPDOWN_OPTION + 'IMAGE');
        case filetypeCheckResult === 'pdf':
            return t(DROPDOWN_OPTION + 'PDF');
        case filetypeCheckResult === 'zip':
            return t(DROPDOWN_OPTION + 'ZIP');
        case filetypeCheckResult === 'presentation':
            return t(DROPDOWN_OPTION + 'PRESENTATION');
        case filetypeCheckResult === 'word':
            return t(DROPDOWN_OPTION + 'WORD');
        case filetypeCheckResult === 'sheet':
            return t(DROPDOWN_OPTION + 'SHEET');
        case filetypeCheckResult === 'audio':
            return t(DROPDOWN_OPTION + 'AUDIO');
        case filetypeCheckResult === 'video':
            return t(DROPDOWN_OPTION + 'VIDEO');
        case filetypeCheckResult === 'folder':
            return t(DROPDOWN_OPTION + 'FOLDER');
        default:
            return t(DROPDOWN_OPTION + 'OTHER');
    }
};

export const getIconName = (contentType: string | null): string => {
    switch (checkFiletype(contentType)) {
        case 'image':
            return 'imagery';
        case 'pdf':
            return 'document-pdf';
        case 'zip':
            return 'document-zip';
        case 'presentation':
            return 'document-ppt';
        case 'word':
            return 'document-doc';
        case 'sheet':
            return 'document-xls';
        case 'audio':
            return 'document-audio';
        case 'video':
            return 'video';
        case 'folder':
            return 'folder-open';
        default:
            return 'document-plain';
    }
};

export const createLocale = (language: string, country: string): string => {
    return language.toLowerCase().concat('_').concat(country.toUpperCase());
};


export const convertLocaleToRepresentationValue = (locale: string): string => {
    if (isNullOrEmpty(locale)) {
        return '';
    }

    let localeChunks = locale.toLowerCase().split('_').reverse();
    if ((localeChunks) && (localeChunks.length >= 2)) {
        return '/'.concat(localeChunks.join('/'));
    }

    return '';
};

export const convertLocaleToPersistentValue = (locale: string): string => {
    if (isNullOrEmpty(locale)) {
        return '';
    }

    let localeChunks = locale.split('_');
    if ((localeChunks) && (localeChunks.length >= 2)) {
        return `${localeChunks[0].toLowerCase()}_${localeChunks[1].toUpperCase()}`;
    }

    return '';
};

export const getFileExtension = (path?: string) => {
    return (path?.match(/[^\\/]\.([^.\\/]+)$/) || [undefined]).pop();
};

export const isWatermarkApplicableCheck = (watermark?: boolean): boolean => {
    if (!config.WTM_ENABLED) {
        return false;
    }

    return (watermark ?? false) as boolean;
};

export const isWatermarkApplicable = (contentType?: string, fileType?: string): boolean => {
    if (!config.WTM_ENABLED) {
        return false;
    }

    let type = (fileType ?? '').toLowerCase();
    if (!isNullOrEmpty(type)) {
        let isApplicable = Object.keys(WatermarkFileType).some(typeKey => (type.includes(typeKey.toLowerCase()))) as boolean;
        if (isApplicable) {
            return isApplicable;
        }
    }

    type = checkFiletype(contentType ?? '').toLowerCase();
    if (!isNullOrEmpty(type)) {
        return Object.keys(WatermarkFileType).some(typeKey => (type.includes(typeKey.toLowerCase()))) as boolean;
    }

    return false;
};

export const isPreviewApplicable = (contentType?: string, fileType?: string): boolean => {
    if (!config.WTM_ENABLED) {
        return false;
    }

    let type = (fileType ?? '').toLowerCase();
    if (!isNullOrEmpty(type)) {
        let isApplicable = Object.keys(PreviewFileType).some(typeKey => (type.includes(typeKey.toLowerCase()))) as boolean;
        if (isApplicable) {
            return isApplicable;
        }
    }

    type = checkFiletype(contentType ?? '').toLowerCase();
    if (!isNullOrEmpty(type)) {
        return Object.keys(PreviewFileType).some(typeKey => type.includes(typeKey.toLowerCase())) as boolean;
    }

    return false;
};
